import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { Checklist } from '@/common/components/Checklist'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { FileUploader } from '@/common/components/FileUploader'
import MaskedTextField from '@/common/components/MaskedTextField'
import { RequestedReview } from '@/common/components/RequestedReview'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import { useCompanyBranch } from '@/common/hooks/queries/useCompanyBranch'
import { ErrorResponse } from '@/common/types/errorResponse'
import { cn } from '@/common/utils/cn'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency, formatDateRange } from '@/common/utils/formatters'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { handleServerError } from '@/common/utils/handleServerError'
import { useAdvanceGovernmentAllocationEntry } from '@/features/governmentAllocations/hooks/useAdvanceGovernmentAllocationEntry'
import { useUpdateGovernmentAllocationEntry } from '@/features/governmentAllocations/hooks/useUpdateGovernmentAllocationEntry'
import { useUpdateGovernmentAllocationEntryFile } from '@/features/governmentAllocations/hooks/useUpdateGovernmentAllocationEntryFile'
import { EntryGovernmentAllocationDetails } from '@/features/governmentAllocations/pages/Update/components/EntryGovernmentAllocationDetails'
import {
  GovernmentAllocationEntryUpdateValidationSchema,
  updateSchema,
} from '@/features/governmentAllocations/schemas/update.schema'
import { GovernmentAllocationEntryData } from '@/features/governmentAllocations/types/governmentAllocationEntry.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { CalendarDaysIcon, SearchIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import CreateEntryProductCategoryModal from '../CreateEntryProductCategoryModal'
import { useClientCompanyBranchAvailableToAllocateSummary } from '@/features/companyBranches/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import { ClientCompanyAllowedCategoriesAndProducts, ClientCompanyAllowedCategoryAndProductData } from '@/features/companyBranches/types/allowedCategoriesAndProducts.type'
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '@/features/companyBranches/services/allowedCategoriesAndProductsTransformer'
import { Option } from '@/common/types/option'
import { filterOptions } from '@/common/constants/filterOptions.constant'
import { FilterOptionOption } from 'react-select'
import useDebounce from '@/common/hooks/useDebounce'

export const GovernmentAllocationEntryCreatedForm = ({
  governmentAllocation,
}: {
  governmentAllocation: GovernmentAllocationEntryData
}) => {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const passedEveryRequirement = governmentAllocation?.requirement_list_summary?.steps?.every((step) => step.passed)

  const [selectedAllowedCategorySummaryRecord, setSelectedAllowedCategorySummary] = useState<ClientCompanyAllowedCategoryAndProductData & { id: string }>()

  const [file, setFile] = useState<Blob>()
  const [fileName, setFileName] = useState('')
  const [baseError, setBaseError] = useState('')

  const useFormMethods = useForm<GovernmentAllocationEntryUpdateValidationSchema>({
    resolver: zodResolver(updateSchema),
  })

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useFormMethods

  const { data: branchData } = useCompanyBranch(governmentAllocation?.government_allocation?.branch_id)

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    data: allowedCategoriesAndProducts,
    isFetching: isAllowedCategoriesAndProductsFetching,
  } = useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyBranchId: governmentAllocation?.government_allocation?.branch_id,
    filters: { search: searchProduct },
  });

  const allowedCategoriesOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsFetching || !allowedCategoriesAndProducts || !allowedCategoriesAndProducts['categories']
      ? []
      : ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({
        key: 'categories',
        controlledBy: governmentAllocation?.government_allocation?.controlled_by,
      })(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsFetching]);

  const onSuccess = () => {
    setBaseError('')
    queryClient.invalidateQueries({ queryKey: ['governmentAllocation', id] })
  }

  const redirectOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['governmentAllocations'] })
    queryClient.invalidateQueries({ queryKey: ['governmentAllocation', id] })
    navigate('/government-allocations')
  }

  const onError = (error: ErrorResponse) => {
    handleServerError({ error, setBaseError, setError })
  }

  const { mutate: updateGovernmentAllocationEntry } = useUpdateGovernmentAllocationEntry({
    governmentAllocationId: id,
    onSuccess,
    onError,
  })

  const { mutate: uploadGovernmentAllocationFile, isPending: uploadGovernmentAllocationFileIsPending } =
    useUpdateGovernmentAllocationEntryFile({
      governmentAllocationId: id,
      onSuccess,
      onError,
    })

  const { mutate: advanceGovernmentAllocationEntry, isPending: advanceGovernmentAllocationEntryIsPending } =
    useAdvanceGovernmentAllocationEntry({
      id,
      onSuccess: redirectOnSuccess,
      onError,
    })

  const onSubmit: SubmitHandler<GovernmentAllocationEntryUpdateValidationSchema> = (data) => {
    clearErrors()
    setBaseError('')
    updateGovernmentAllocationEntry(data)
  }

  const submit = handleSubmit(onSubmit)

  const submittableOnBlur = (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => {
    if (e.target.value === '') {
      return
    }
    onBlur(e)
    submit()
  }

  useEffect(reset, []);

  return (
    <Box className="flex gap-6 items-start h-[calc(100vh-130px)] shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
      <div className="w-full flex flex-col gap-6 h-[calc(100vh-178px)] overflow-y-auto">
        {baseError && <p className="text-sm text-danger-soft">{baseError}</p>}

        <p className="text-lg font-bold">
          Detalhes {governmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
        </p>

        {governmentAllocation?.last_review?.state === 'disapproved' && (
          <RequestedReview description={governmentAllocation?.last_review?.description} />
        )}

        <div className="flex gap-6">
          <div>
            <p className="text-sm font-light italic">Cota da de empenho da filial</p>
            <p className="text-lg font-bold">{formatCurrency(Number(branchData?.granted_credit || 0))}</p>
          </div>
        </div>

        {governmentAllocation?.government_allocation?.kind === 'pre_paid' ? (
          <div className="flex gap-x-2">
            <Controller
              control={control}
              name="office_number"
              defaultValue={governmentAllocation?.office_number}
              disabled={advanceGovernmentAllocationEntryIsPending}
              render={({ field: { onBlur, ...rest } }) => (
                <TextField
                  label="Número do ofício do Empenho"
                  className="mt-5"
                  {...rest}
                  errorMessage={errors.office_number?.message}
                  onBlur={(e) => submittableOnBlur?.(e, onBlur)}
                />
              )}
            />

            <div className="space-y-2">
              <p className="text-[11px] leading-3">Período de competência</p>

              <div className="flex items-start gap-x-2">
                <Controller
                  control={control}
                  name="government_allocation.start_of_document_validity"
                  defaultValue={
                    governmentAllocation?.government_allocation?.start_of_document_validity
                      ? dateTransformer(governmentAllocation?.government_allocation?.start_of_document_validity)
                      : undefined
                  }
                  disabled={advanceGovernmentAllocationEntryIsPending}
                  render={({ field: { onBlur, ...rest } }) => (
                    <MaskedTextField
                      label="Início"
                      mask="00/00/0000"
                      LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                      {...rest}
                      errorMessage={errors.government_allocation?.start_of_document_validity?.message}
                      onBlur={(e) => submittableOnBlur?.(e, onBlur)}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="government_allocation.end_of_document_validity"
                  defaultValue={
                    governmentAllocation?.government_allocation?.end_of_document_validity
                      ? dateTransformer(governmentAllocation?.government_allocation?.end_of_document_validity)
                      : undefined
                  }
                  disabled={advanceGovernmentAllocationEntryIsPending}
                  render={({ field: { onBlur, ...rest } }) => (
                    <MaskedTextField
                      label="Fim"
                      mask="00/00/0000"
                      LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                      {...rest}
                      errorMessage={errors.government_allocation?.end_of_document_validity?.message}
                      onBlur={(e) => submittableOnBlur?.(e, onBlur)}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div>
              <p className="text-sm font-light italic">N da cota estimativa</p>
              <p className="text-lg font-bold">{governmentAllocation?.office_number}</p>
            </div>

            <div>
              <p className="text-sm font-light italic">Período de competência</p>
              <p className="text-lg font-bold">
                {governmentAllocation?.government_allocation?.start_of_document_validity &&
                  governmentAllocation?.government_allocation?.end_of_document_validity &&
                  formatDateRange(
                    new Date(governmentAllocation?.government_allocation?.start_of_document_validity),
                    new Date(governmentAllocation?.government_allocation?.end_of_document_validity),
                  )}
              </p>
            </div>
          </>
        )}

        {governmentAllocation?.government_allocation?.kind === 'pre_paid' && (
          <FileUploader
            name="file"
            accept="application/pdf, image/png, image/jpg, image/jpeg"
            title="Anexe o Empenho"
            fileName={
              fileName
                ? fileName
                : governmentAllocation?.file?.url
                  ? getFilenameFromUrl(governmentAllocation?.file?.url)
                  : undefined
            }
            currentFileUrl={
              file
                ? URL.createObjectURL(file)
                : governmentAllocation?.file?.url
                  ? governmentAllocation?.file?.url
                  : undefined
            }
            handleChange={(file) => {
              setFileName(file.name)
              setFile(file)
              uploadGovernmentAllocationFile(file)
            }}
            disabled={uploadGovernmentAllocationFileIsPending || advanceGovernmentAllocationEntryIsPending}
          />
        )}

        <Controller
          name="total_value"
          control={control}
          defaultValue={
            governmentAllocation?.total_value ? formatCurrency(Number(governmentAllocation?.total_value)) : undefined
          }
          disabled={advanceGovernmentAllocationEntryIsPending}
          render={({ field: { onBlur, ...rest } }) => (
            <CurrencyTextField
              label={`Valor total ${governmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do empenho' : 'da cota'
                }`}
              markAsRequired
              maxValue={999_999_999.99}
              className="w-full"
              {...rest}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
              errorMessage={errors.total_value?.message}
            />
          )}
        />

        <Accordion
          className="py-1"
          head={
            <p>
              Detalhamento {governmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
            </p>
          }
          body={
            <div className="space-y-4">
              <Select
                placeholder={
                  <div
                    className={cn(
                      'flex items-center gap-x-2',
                      !advanceGovernmentAllocationEntryIsPending && 'text-white-800',
                    )}
                  >
                    <SearchIcon />
                    <p>Pesquisar serviços</p>
                  </div>
                }
                isLoading={isAllowedCategoriesAndProductsFetching}
                options={
                  allowedCategoriesOptions?.filter((productCategoryOption) => {
                    return !governmentAllocation.entry_product_categories?.some(
                      ({ product_category }) => product_category?.id === productCategoryOption.value,
                    );
                  }) || []
                }
                onInputChange={setSearchProduct}
                filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
                value=""
                onChange={(newValue) => {
                  setSelectedAllowedCategorySummary({
                    ...allowedCategoriesAndProducts?.categories?.[(newValue as Option).value],
                    id: (newValue as Option).value,
                  })
                }}
                isDisabled={
                  advanceGovernmentAllocationEntryIsPending
                }
              />

              {!!governmentAllocation.entry_product_categories.length && (
                <EntryGovernmentAllocationDetails
                  companyBranchId={governmentAllocation?.government_allocation?.branch_id}
                  governmentAllocationId={governmentAllocation.id}
                  entryProductCategories={governmentAllocation.entry_product_categories}
                  disabled={advanceGovernmentAllocationEntryIsPending}
                  controlledBy={governmentAllocation?.government_allocation?.controlled_by}
                  governmentAllocationCurrencyRestricted={governmentAllocation?.government_allocation?.currency_restricted}
                />
              )}
            </div>
          }
        />
      </div>

      <Checklist
        requirement_list_summary={governmentAllocation?.requirement_list_summary}
        className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
        Buttons={() => (
          <Button
            type="button"
            variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
            disabled={!passedEveryRequirement && !advanceGovernmentAllocationEntryIsPending}
            onClick={() => advanceGovernmentAllocationEntry()}
            isLoading={advanceGovernmentAllocationEntryIsPending}
          >
            Enviar para análise
          </Button>
        )}
      />

      <CreateEntryProductCategoryModal
        companyBranchId={governmentAllocation?.government_allocation?.branch_id}
        allowedCategorySummary={selectedAllowedCategorySummaryRecord}
        onDismiss={() => setSelectedAllowedCategorySummary(undefined)}
        controlledBy={governmentAllocation?.government_allocation?.controlled_by}
        governmentAllocationCurrencyRestricted={governmentAllocation?.government_allocation?.currency_restricted}
      />
    </Box>
  )
}
